// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, type ReactNode, useCallback } from 'react';

import {
  useEvolveDelete,
  useEvolvePost,
  useEvolvePut,
  type HexTriplet,
  type WorkOrderStatus,
  type WorkOrderStatusId,
} from '@evolvemep/api-ts';

import type { CustomStatus, CustomStatusesHookValue, EntityState } from 'components/Mantine/StatusEditor/types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

type WorkOrderStatusesContextType = CustomStatusesHookValue<'Work Order'>;

const normalizeStatus = (status: WorkOrderStatus): CustomStatus<'Work Order'> => ({
  statusId: status.workOrderStatusId,
  stateId: status.workOrderStateId,
  name: status.name,
  color: status.color,
});

const WorkOrderStatusesContext = React.createContext<WorkOrderStatusesContextType | undefined>(undefined);

export const WorkOrderStatusesProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: workOrderStatesData,
    updateStoredValue: setWorkOrderStatesData,
    loading,
  } = useEvolveGetWithLocalStorage('shop/workOrder/states');

  const states = useMemo<EntityState<'Work Order'>[] | undefined>(
    () =>
      workOrderStatesData?.map((state) => ({
        stateId: state.workOrderStateId,
        name: state.name,
        statuses: state.statuses.map(normalizeStatus),
      })),
    [workOrderStatesData],
  );

  const statuses = useMemo<CustomStatus<'Work Order'>[]>(
    () => states?.flatMap((state) => state.statuses) ?? [],
    [states],
  );

  const statusMap = useMemo<Record<WorkOrderStatusId, CustomStatus<'Work Order'>>>(
    () => statuses.reduce((out, current) => ({ ...out, [current.statusId]: current }), {}),
    [statuses],
  );

  const { apiCall: addStatusApiCall, loading: addingStatus } = useEvolvePost('shop/workOrder/status');
  const addStatus = useCallback<WorkOrderStatusesContextType['addStatus']>(
    async (body) => {
      const created = await addStatusApiCall({
        workOrderStateId: body.stateId,
        name: body.name,
        color: body.color,
      });
      setWorkOrderStatesData((current) =>
        current?.map((state) =>
          state.workOrderStateId === created.workOrderStateId
            ? { ...state, statuses: [...state.statuses, created] }
            : state,
        ),
      );
      return normalizeStatus(created);
    },
    [addStatusApiCall, setWorkOrderStatesData],
  );

  const { apiCall: editStatusApiCall, loading: editingStatus } = useEvolvePut(
    `shop/workOrder/status/:workOrderStatusId`,
  );
  const updateStatus = useCallback<WorkOrderStatusesContextType['updateStatus']>(
    async (statusId, body: { name: string; color: HexTriplet }) => {
      const updated = await editStatusApiCall(body, {
        url: `shop/workOrder/status/${statusId}`,
      });
      setWorkOrderStatesData((current) =>
        current?.map((state) =>
          state.workOrderStateId === updated.workOrderStateId
            ? {
                ...state,
                statuses: state.statuses.map((status) => (status.workOrderStatusId === statusId ? updated : status)),
              }
            : state,
        ),
      );
      return normalizeStatus(updated);
    },
    [editStatusApiCall, setWorkOrderStatesData],
  );

  const { apiCall: deleteStatusApiCall, loading: deletingStatus } = useEvolveDelete(
    `shop/workOrder/status/:workOrderStatusId`,
  );
  const deleteStatus = useCallback<WorkOrderStatusesContextType['deleteStatus']>(
    async (statusId: WorkOrderStatusId) => {
      await deleteStatusApiCall({
        url: `shop/workOrder/status/${statusId}`,
      });
      setWorkOrderStatesData((current) =>
        current?.map((state) => ({
          ...state,
          statuses: state.statuses.filter((status) => status.workOrderStatusId !== statusId),
        })),
      );
    },
    [deleteStatusApiCall, setWorkOrderStatesData],
  );

  const mutationRunning = addingStatus || editingStatus || deletingStatus;

  const value = useMemo<WorkOrderStatusesContextType>(
    () => ({ states, statuses, statusMap, loading, addStatus, updateStatus, deleteStatus, mutationRunning }),
    [addStatus, deleteStatus, loading, mutationRunning, states, statusMap, statuses, updateStatus],
  );

  return <WorkOrderStatusesContext value={value}>{children}</WorkOrderStatusesContext>;
};

export const useWorkOrderStatuses = () => useGeneralContext(WorkOrderStatusesContext, 'WorkOrderStatuses');
